import {
  adminMotorVars,
  defaultMotorVars,
  deleteMotors,
  ecopetrolMotorVars,
  kocMotorVars,
  restoreMotors
} from "@/models/equipment/motors/predicates";
import type { Motors } from "gql/api";
import {
  useDeleteMotorsMutation,
  useInsertMotorMutation,
  useMotorsQuery,
  useRestoreMotorsMutation,
  useUpdateMotorByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import type { ComputedRef } from "vue";
import { computed } from "vue";
import { useEquipment } from "../baseEquipment";
import type { UseEquipment } from "../equipmentTypes";
import type { MotorRowData } from "./motorTableColumns";
import { motorTableColumns } from "./motorTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";
import { get50HzNameplate } from "@/libs/calculations/motors";

export function useMotors(isAdmin: boolean): UseEquipment<Motors> {
  const baseEquipment = useEquipment<Motors>(isAdmin);
  const title = "Motors";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocMotorVars;
    if (user.is_ecopetrol_customer) return ecopetrolMotorVars;
    if (user.is_admin) return adminMotorVars;
    return defaultMotorVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useMotorsQuery({
    variables: getVariables()
  });

  const motors = computed(() => data.value?.motors as Motors[] | undefined);

  const allRows: ComputedRef<MotorRowData[]> = computed(() => {
    return (
      motors.value?.map((m: Motors) => {
        return {
          ...m,
          nameplate_horsepower_50: get50HzNameplate(m.nameplate_horsepower),
          nameplate_voltage_50: get50HzNameplate(m.nameplate_voltage)
        };
      }) || []
    );
  });

  const hashOrder: (keyof Motors)[] = [
    "manufacturer",
    "part_number",
    "nameplate_horsepower",
    "nameplate_voltage",
    "nameplate_current",
    "outer_diameter_inches"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertMotor } = useInsertMotorMutation();
  const { executeMutation: executeUpdateMotor } = useUpdateMotorByPkMutation();
  const { executeMutation: executeDeleteMotor } = useDeleteMotorsMutation();
  const { executeMutation: executeRestoreMotor } = useRestoreMotorsMutation();

  const insertMotor = (
    motorRowData: Omit<MotorRowData, "sizing_import" | "user">
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { nameplate_horsepower_50, nameplate_voltage_50, ...motor } =
      motorRowData;
    return executeInsertMotor({ object: motor });
  };

  const updateMotor = (id: string, motorRowData: MotorRowData) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { nameplate_horsepower_50, nameplate_voltage_50, ...motor } =
      motorRowData;
    return executeUpdateMotor({ id, object: motor });
  };
  const deleteMotor = (ids: string[]) =>
    executeDeleteMotor({ where: deleteMotors(ids) });

  const restoreMotor = (ids: string[]) =>
    executeRestoreMotor({ where: restoreMotors(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "motors",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    allow_rerate: true,
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: "",
    part_number: "",
    outer_diameter_inches: null,
    inner_diameter_inches: null,
    neck_outer_diameter_inches: null,
    neck_inner_diameter_inches: null,
    length_inches: null,
    weight_pounds: null,
    mle_offset_depth_inches: 0,
    outer_diameter_rub_inches: 0,
    nameplate_horsepower: null,
    nameplate_horsepower_50: null,
    nameplate_voltage: null,
    nameplate_voltage_50: null,
    nameplate_current: null,
    poles: null,
    configuration: "",
    shaft_horsepower: null,
    temperature_rating_fahrenheit: null,
    oil_volume_gallons: null,
    nb: null,
    n1: null,
    n2: null,
    n3: null,
    n4: null,
    n5: null,
    ib: null,
    i1: null,
    i2: null,
    i3: null,
    i4: null,
    i5: null,
    pfb: null,
    pf1: null,
    pf2: null,
    pf3: null,
    pf4: null,
    pf5: null,
    effb: null,
    eff1: null,
    eff2: null,
    eff3: null,
    eff4: null,
    eff5: null,
    nv0: 1,
    nv1: 0,
    nv2: 0,
    iv0: 1,
    iv1: 0,
    iv2: 0,
    pfv0: 1,
    pfv1: 0,
    pfv2: 0,
    effv0: 1,
    effv1: 0,
    effv2: 0,
    tv0: 1,
    tv1: 0,
    tv2: 0,
    rr_vb: null,
    rr_v1: null,
    rr_v2: 0,
    rr_v3: 0,
    rr_lb: null,
    rr_l1: null,
    rr_l2: null,
    is_hps: false
  }));

  return {
    searchPlaceholder: "Search Motors...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "375 DMP"`,
    columns: defineVisibleColumns(motorTableColumns),
    handleUpsert: defineUpsert(insertMotor, updateMotor),
    handleDelete: defineDelete(deleteMotor),
    handleRestore: defineRestore(restoreMotor),
    totalCount: computed(() => data.value?.motors_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: motorTableColumns,
      globalSearchFields: [
        "manufacturer",
        "nameplate_horsepower",
        "part_number"
      ],
      allRows,
      title
    })
  };
}
